// Копирайт онбординга (входы A и B) — spec/08_copy.md, разделы «Онбординг».
// Голос — вариант A «своя в чате» (docs/copy-variants.md). Исключение — строки про лучик:
// они везде в приложении звучат ясно и тепло, без мема (выбор основателя).
//
// Вводного «Так,» здесь больше нет ни в одной строке (правка основателя после теста): оно
// разошлось по всему онбордингу и читалось как тик, а не как характер. Заменено разными
// зачинами — механической замены одного слова другим не делаем, иначе выйдет та же беда.
//
// Пол здесь двух разных людей, и путать их нельзя:
//   • вход A — пол самого человека, он лежит в черновике онбординга (`OnboardingDraft.gender`,
//     шаг A1) и до конца флоу на сервер ещё не уехал;
//   • вход B — пол пригласившего, он приходит из превью приглашения (`InvitePreviewQuery`).
// Абстрактные близкие из обучающих подписей («подруга», «мама») пола не имеют вовсе — связей
// на этом шаге ещё нет, — поэтому фразы о них написаны без рода.
import { textByGender } from '@lunya/contracts'

import type { InviteCopy, OnbCopy } from './types'

export const onb: OnbCopy = {
  a1: {
    title: 'О, привет. Я Луня 🐾',
    // «Теперь ты тоже моя» — тоже о самом человеке. Пол на этом шаге ещё не отправлен на
    // сервер, но он уже угадан по имени (`useGenderGuess`), а пока имя не введено — не угадан
    // вовсе, и тогда фраза обходится без рода, а не берёт женский по умолчанию.
    // «Теперь ты тоже моя» звучало присвоением (правка основателя). Рода в новой строке
    // нет вовсе, поэтому пара gendered/neutral тут больше ничего не различает.
    subtitle: () => 'Читаю звёзды, приглядываю за своими. Теперь мы вместе!',
    // «Звать тебя» — реплика Луни, и на экране приглашения она читалась началом фразы,
    // а не подписью поля (правка основателя). Строка одна на вход A и вход B, поэтому
    // подпись стала нейтральной сразу в обоих.
    name_label: 'Твоё имя',
    name_hint: 'Измени, если промазала',
    name_empty: 'И как тебя звать?',
    name_error: 'Ну хоть как-то же тебя звать 🥺',
    cta: 'Погнали ✨',
    cta_editing: 'Всё верно ✨',
    gender_q: 'Ты у нас…?',
  },
  a2: {
    title: ({ name, gender }) =>
      `${name}, ${textByGender(gender, 'когда ты появил{ся|ась} на свет?', 'когда у тебя день рождения?')}`,
    subtitle: 'Самое важное. Без даты я слепая как крот, а я, между прочим, спаниель.',
    date: 'Дата рождения',
    // Одна строка на поле и на заголовок шторки: капс делает ValueField через text-transform.
    // Хвосты «для полной карты · можно потом» из 08_copy убраны — подпись живёт внутри поля
    // и в одну строку не помещалась. Мысль «можно потом» несёт строка `later`.
    time: 'Время рождения',
    city: 'Город рождения',
    later: 'Если не хочешь сейчас заполнять время и город, потом сможешь дополнить, переживу',
    cta: 'Ну, показывай ✨',
    err_future: 'Из будущего? Уважаю. Но давай честно.',
    err_age_title: 'Луня дружит с теми, кто постарше',
    err_age_cta: 'Изменить дату',
    err_age_body: 'Приходи в четырнадцать. Я никуда не денусь.',
  },
  a3: {
    overline: 'ГОТОВО',
    title: 'Смотри, чего я нашла',
    // Было «ТЫ ПО ЛУНЕ» — и это была ошибка по сути: знак зодиака задаётся Солнцем, а лунный
    // знак — отдельная величина (теперь она под трактовкой, своей плашкой). Формулировка
    // временная: точную выбирает сплошной аудит терминологии, идущий параллельно.
    sun_card: 'ТЫ ПО СОЛНЦУ',
    // Что открылось от введённых данных. Голос Луни здесь выключен (правило 21): подписи
    // и оговорка говорят ровно то, что посчитано, и вслух называют границу расчёта.
    more: {
      element: 'СТИХИЯ',
      element_sub: 'стихия знака Солнца',
      moon: 'ЛУНА',
      moon_sub: 'эмоции и отдых',
      asc: 'АСЦЕНДЕНТ',
      asc_sub: ({ gender }) =>
        textByGender(gender, 'как{им|ой} тебя видят сначала', 'что видят первым'),
      // Ступени — те же, по которым синастрия описывает, чем расчёт располагал
      // (`resolveCompatPrecision`). Дата на этом шаге есть всегда (без неё шаг A2 не пускает
      // дальше), поэтому `sign_only` сюда не приходит и читается как `date_only`.
      note: ({ precision }) => {
        if (precision === 'exact')
          return 'Асцендент — точка, которая восходила на горизонте в минуту рождения; он считается только по времени и городу. Знак Луны здесь тоже точный.'
        if (precision === 'time_no_city')
          return 'Без города рождения часовой пояс подставлен по умолчанию: у границы знак Луны может оказаться соседним.'
        return 'Без времени рождения Луна посчитана на полдень: за сутки она проходит около 13°, поэтому у границы знак может оказаться соседним.'
      },
      // Расчёт не доехал. Ничего не обещаем: будет ли асцендент в натальной карте, зависит
      // от тех же времени и города, а не от повторной попытки.
      failed: 'Луну и асцендент сейчас не посчитать — расчёт не доехал.',
    },
    // Без хвоста «→ дополнить»: он выглядел ссылкой, но никуда не вёл (правка основателя).
    //
    // Подпись зависит от того, что человек ввёл на шаге A2. Асцендент и дома считаются только
    // при наличии И времени, И города (город даёт координаты и часовой пояс), поэтому строка
    // называет ровно недостающее, а при полных данных не выпрашивает ничего, а зовёт смотреть
    // разбор. Раньше тут всегда висело «Скажешь время — расскажу больше» — даже тем, кто время
    // указал (правка основателя).
    upsell: ({ hasBirthTime, hasBirthCity }) => {
      if (hasBirthTime && hasBirthCity)
        return 'Всё для асцендента у меня есть — разбор целиком в твоей натальной карте'
      if (hasBirthTime) return 'Остался город рождения — без него асцендент не посчитаю'
      if (hasBirthCity) return 'Осталось время рождения — без него асцендент не посчитаю'
      return 'Скажешь время и город — расскажу больше'
    },
    forecast_card: 'ПРЕДСКАЗАНИЕ НА СЕГОДНЯ',
    seed: 'Завтра спрошу, сбылось ли. Я веду счёт 😤',
    // Блок «Что я умею» с роликом вместо схемы про узы (правка основателя): показать
    // возможности живой картинкой убедительнее, чем объяснить их тремя строчками текста.
    abilities_card: 'ЧТО Я УМЕЮ',
    cta: 'А дальше?',
  },
  bridge: {
    // «Одной скучно» — про самого человека, и это была женская форма для всех: мужчина читал
    // о себе в женском роде прямо над строкой, которая склоняется правильно (правка основателя).
    title: ({ gender }) => textByGender(gender, '{Одному|Одной} скучно', 'Вдвоём интереснее'),
    body: ({ gender }) =>
      textByGender(
        gender,
        'Добавь подругу — покажу, как вы об{а|е}. У неё тучи — ты узнаешь перв{ым|ой}. И наоборот.',
        'Добавь подругу — покажу вас двоих. У неё тучи — ты узнаешь сразу. И наоборот.',
      ),
    cta: 'Позвать подругу 💫',
    skip: 'Потом',
  },
  b0: {
    // «Тянет тебя к себе на небо» читалось двусмысленно — вплоть до «забирает на тот свет»
    // (правка основателя). Небесной метафоры в приглашении больше нет вовсе: человек ещё
    // ничего не знает о продукте, и первая фраза обязана быть буквальной.
    title: ({ inviter }) => `${inviter} приглашает тебя!`,
    // Род тут — ПРИГЛАСИВШЕГО, а не читателя: «радовать его комплиментами» говорит о нём.
    // Пол приходит из превью приглашения (`InvitePreview.inviterGender`) и может быть
    // неизвестен — тогда фраза обходится без него, а не берёт мужской по умолчанию.
    subtitle: ({ inviter, gender }) =>
      textByGender(
        gender,
        `Ты сможешь видеть настроение и радовать {его|её} комплиментами! А ${inviter} тебя!`,
        `Ты сможешь видеть настроение и радовать комплиментами! А ${inviter} тебя!`,
      ),
    cta: ({ inviter }) => `Хочу к ${inviter} ✨`,
  },
  b3: {
    title: ({ inviter }) => `Готово. Вы с ${inviter} теперь вместе 💛`,
    body: ({ gender }) =>
      textByGender(
        gender,
        'Отмечайся раз в день — {он|она} увидит. И ты увидишь {его|её}.',
        'Отмечайся раз в день — тебя увидят. И ты увидишь в ответ.',
      ),
    cta: 'Хорошо',
  },
}

export const invite: InviteCopy = {
  // Та же правка, что на B0: «утащил тебя к себе на небо» звучало так, будто человека
  // забирают на тот свет. Формулировка теперь совпадает с заголовком экрана, на который
  // ведёт карточка, — читатель видит одно и то же дважды, а не две разные метафоры.
  // Рода в новой строке нет, поэтому пара `gendered`/`neutral` тут больше ничего не делает
  // и обе половины одинаковы: сигнатуру оставляем, чтобы не переписывать вызовы.
  card: ({ name }) => `${name} приглашает тебя!`,
  message: ({ name }) => `${name} хочет, чтобы Луня приглядывала и за тобой 🐾`,
  button: 'Посмотреть',
}
